// Single source of truth for the Platform Features / network-participation item
// list shared by the Settings → Platform Features card (NetworkParticipationCard.js),
// the per-row edit modal (NetworkParticipationModal.js), and the admin matrix
// (NetworkParticipationMatrix.js).
//
// `section` groups the rows: 'platform' items are platform capabilities
// (Scheduler & Appointments, AI Report) rendered ahead of the 'network' referral
// partners, which stay in alphabetical order.
//
// `kind` picks the save endpoint: 'scheduler' → updateEnableSchedulerForCalibrationCenter,
// 'ai-report' → updateEnableAiReportForCalibrationCenter, 'referral' →
// updateReferralSourceToggle(ccId, isEnabled, key). `flagField` is the boolean
// returned by getCalibrationCenter for the current on/off state.
//
// `tableLabel` is the short header used by the Network Participation matrix columns
// (the modal uses the longer `name`). `showInTable: false` keeps an enrollable
// carrier out of the matrix and card while still exposing it as an edit-modal
// toggle — used for Farmers, which is not yet a live national agreement.
export const NETWORK_PARTICIPATION_ITEMS = [
  // Platform features — always listed ahead of the referral networks.
  {
    key: 'ACCOUNT_STATUS',
    name: 'Location Status',
    tableLabel: 'Location Status',
    flagField: 'is_active',
    kind: 'account-status',
    section: 'platform',
    // Short line rendered under the name on the Platform Features card. State-aware:
    // `subtitle` shows while active, `subtitleInactive` while inactive.
    subtitle: 'This location is active and its users can sign in.',
    subtitleInactive: 'This location is inactive and its users cannot sign in.',
    // Mirrors the Settings → Account → Edit active/inactive control. Persisted via
    // the whole-center endpoint (createCalibrationCenter), not a dedicated flag
    // toggle. Kept off the admin matrix (`showInMatrix: false`), which already
    // surfaces active status as its own pill + All/Active filter, so a column here
    // would be redundant.
    showInMatrix: false,
    description: 'Marks this location as active or inactive. Mirrors the Active / Inactive control on the Account settings page.'
  },
  {
    key: 'SCHEDULER',
    name: 'Scheduler & Appointments',
    tableLabel: 'Scheduler & Appts',
    flagField: 'is_scheduler_enabled',
    kind: 'scheduler',
    section: 'platform',
    subtitle: 'Enabling activates the Scheduler feature for users.',
    description:
      'Activates Scheduler, Calendar, and online appointment booking for this location. When enabled, Scheduler appears in the left navigation and the external scheduling link becomes active.'
  },
  {
    key: 'AI_REPORT',
    name: 'AI Report',
    tableLabel: 'AI Report',
    flagField: 'is_ai_report_enabled',
    kind: 'ai-report',
    section: 'platform',
    subtitle: 'Enabling activates the AI Report feature for users.',
    description: 'Enables ADAS Intelligence AI reporting for this location.'
  },
  // Referral partner networks — keep alphabetical by name.
  {
    key: 'AS',
    name: 'Allstate',
    tableLabel: 'Allstate',
    flagField: 'is_allstate_enabled',
    kind: 'referral',
    section: 'network',
    description: 'Calibration Center will participate in referral network and receive calibration appointments scheduled from Allstate.'
  },
  {
    key: 'BRI',
    name: 'Bear River Insurance',
    tableLabel: 'Bear River',
    flagField: 'is_bearriver_enabled',
    kind: 'referral',
    section: 'network',
    description:
      'Calibration Center will participate in referral network and receive calibration appointments scheduled from Bear River Insurance.'
  },
  {
    key: 'CSAA',
    name: 'CSAA Insurance Group',
    tableLabel: 'CSAA',
    flagField: 'is_csaa_enabled',
    kind: 'referral',
    section: 'network',
    description: 'Calibration Center will participate in referral network and receive calibration appointments scheduled from CSAA'
  },
  {
    key: 'ERAC',
    name: 'Enterprise (ERAC)',
    tableLabel: 'Enterprise',
    flagField: 'is_erac_enabled',
    kind: 'referral',
    section: 'network',
    description:
      'Calibration Center will participate in referral network and receive calibration appointments scheduled from Enterprise Rent-A-Car.'
  },
  {
    key: 'FARMERS',
    name: 'Farmers',
    tableLabel: 'Farmers',
    flagField: 'is_farmers_enabled',
    kind: 'referral',
    section: 'network',
    // Farmers is enrollable in the edit modal but not yet a live national agreement,
    // so it is intentionally excluded from the matrix columns and the card list.
    showInTable: false,
    description: 'Calibration Center will participate in referral network and receive calibration appointments scheduled from Farmers.'
  },
  {
    key: 'GEICO',
    name: 'GEICO',
    tableLabel: 'GEICO',
    flagField: 'is_geico_enabled',
    kind: 'referral',
    section: 'network',
    description: 'Calibration Center will participate in referral network and receive calibration appointments scheduled from GEICO.'
  },
  {
    key: 'HANOVER',
    name: 'Hanover',
    tableLabel: 'Hanover',
    flagField: 'is_hanover_enabled',
    kind: 'referral',
    section: 'network',
    description:
      'Calibration Center will participate in referral network and receive calibration appointments scheduled from The Hanover Insurance Group.'
  },
  {
    key: 'NODAK',
    name: 'Nodak Insurance',
    tableLabel: 'Nodak',
    flagField: 'is_nodak_enabled',
    kind: 'referral',
    section: 'network',
    description:
      'Calibration Center will participate in referral network and receive calibration appointments scheduled from Nodak Insurance.'
  },
  {
    key: 'PROGRESSIVE',
    name: 'Progressive',
    tableLabel: 'Progressive',
    flagField: 'is_progressive_enabled',
    kind: 'referral',
    section: 'network',
    description: 'Calibration Center will participate in referral network and receive calibration appointments scheduled from Progressive.'
  },
  {
    key: 'SF',
    name: 'State Farm',
    tableLabel: 'State Farm',
    flagField: 'is_sf_enabled',
    kind: 'referral',
    section: 'network',
    description: 'Calibration Center will participate in referral network and receive calibration appointments scheduled from State Farm.'
  }
]

// Flag columns for the Network Participation matrix, derived from the shared item
// list so every enrollable carrier (CSAA in particular) surfaces automatically.
// Platform-feature columns come first, then the alphabetized networks.
// `showInTable: false` items (Farmers) are excluded, as are `showInMatrix: false`
// items (Account Status, which the matrix already renders as its own pill/filter).
// This is the single source of truth for NETWORK_TABLE_HEADERS.
export const NETWORK_TABLE_FLAG_COLUMNS = NETWORK_PARTICIPATION_ITEMS.filter(
  (item) => item.showInTable !== false && item.showInMatrix !== false
).map((item) => ({
  id: item.flagField,
  label: item.tableLabel || item.name,
  kind: 'flag'
}))

// A referral network is available only while its referral source is globally
// enabled (referral_source.is_enabled). The backend signals this by omitting the
// network's flag key from calibration-center responses when the source is
// disabled — so availability is "the flag key is present in the response".
// Platform features (scheduler, AI report) are always available.
export const isNetworkItemAvailable = (item, centerData) =>
  item.section !== 'network' || (centerData != null && item.flagField in centerData)
